import {
	Component,
	ComponentInterface,
	Element,
	h,
	Host,
	Listen,
	Prop,
} from '@stencil/core';
import { ToggleBarLayoutMode } from '../_types/toggle';
import { getChildComponent } from '../_helpers/prefixes';
import isChromatic from 'chromatic/isChromatic';

const WindowResizeDebounceDelay = 200;
const SingleLineHeight = 48;

@Component({
	tag: 'ds-toggle-bar',
})
export class ToggleBar implements ComponentInterface {
	setLayoutTimer: number;

	@Prop() appearance: 'primary' | 'secondary' | 'tertiary' = 'primary';
	@Prop() appendedScreenReaderMsgWhenOn: string;
	@Prop() appendedScreenReaderMsgWhenOff: string;
	@Prop({ reflect: true, mutable: true }) layout: ToggleBarLayoutMode =
		ToggleBarLayoutMode.SingleLine;
	@Element() el: HTMLDsToggleBarElement;
	toggleBarButton: HTMLButtonElement;
	toggleBarText: HTMLDivElement;
	toggleSwitch: HTMLDsToggleSwitchElement;

	componentDidRender(): Promise<void> | void {
		// Disable nested component focus state - we will handle in this component instead
		this.toggleSwitch = getChildComponent(this.el, 'ds-toggle-switch');
		this.toggleSwitch.focusable = false;

		// Locate some elements needed for toggleSwitchHandler() and setAriaDynamicAriaLabel()
		this.toggleBarButton = this.el.querySelector('#ds-toggle-bar-button');
		this.toggleBarText = this.el.querySelector('.ds-toggle-bar-text');
	}

	@Listen('toggleSwitch')
	toggleSwitchHandler(event: CustomEvent) {
		if (this.toggleBarButton) {
			this.setAriaDynamicAriaLabel(event.detail.on);
			this.toggleBarButton.setAttribute('aria-checked', event.detail.on);
		}
	}

	/**
	 * Note: because this is invoked from a <button>, if you are focused and press enter, this is also invoked
	 */
	@Listen('click')
	clickHandler() {
		this.toggleSwitch.toggle();
	}

	@Listen('resize', { target: 'window' })
	async windowResizeHandler() {
		if (this.setLayoutTimer) {
			window.clearTimeout(this.setLayoutTimer);
		}
		this.setLayoutTimer = window.setTimeout(
			() => this.setLayout(),
			WindowResizeDebounceDelay
		);
	}

	/**
	 * Dynamically create an aria label as an alternative to the visiable text, for screenreaders to read out.
	 * This is optional and not necessary required in order to use this component in an accessible way, because if these
	 * messages are not provided, screenreaders will still read out the standard text.
	 *
	 * @param on: boolean   Whether or not the aria label is being set during an on action (otherwise it's an off action)
	 */
	setAriaDynamicAriaLabel(on: boolean) {
		if (
			!this.appendedScreenReaderMsgWhenOn ||
			!this.appendedScreenReaderMsgWhenOff
		) {
			// Both messages must be set or we ignore them. This is so that aria-label never gets set, and the normal text will be read out instead.
			return;
		}
		if (on) {
			this.toggleBarText.setAttribute(
				'aria-label',
				this.toggleBarText.textContent +
					'. ' +
					this.appendedScreenReaderMsgWhenOn
			);
		} else {
			this.toggleBarText.setAttribute(
				'aria-label',
				this.toggleBarText.textContent +
					'. ' +
					this.appendedScreenReaderMsgWhenOff
			);
		}
	}

	/**
	 * Set the layout mode, to optimise the CSS applied depending on the height of the component
	 */
	setLayout() {
		if (!isChromatic()) {
			if (this.el.offsetHeight > SingleLineHeight) {
				this.layout = ToggleBarLayoutMode.MultiLine;
			} else {
				this.layout = ToggleBarLayoutMode.SingleLine;
			}
		}
	}

	render() {
		return (
			<Host appearance={this.appearance}>
				<slot></slot>
			</Host>
		);
	}
}
